import React, { useMemo } from 'react';
import SearchSelect from './SearchSelect';

/**
 * client#19: one searchable line-item picker for every page that selects a service / line item / agreement line —
 * search by name OR code (NDIS support item number, catalogue code, category).
 *
 * Give it raw items (service catalogue rows, branch catalogue items: { id|_id, name, code?, ndis_support_item_number?,
 * support_item_number?, rate_per_unit?|rate?|hourly_rate? }) via `items`, or ready-made SearchSelect options
 * ({ value, label, keywords?, disabled? }, e.g. agreement-line PSIs) via `options`.
 */
export function lineItemOption(item, { showRate = true, rateSuffix = '' } = {}) {
  if (!item) return null;
  if (item.value !== undefined && item.label !== undefined && item.name === undefined) {
    // already an option (agreement line / PSI): its label carries the support item number
    return { ...item, keywords: [item.keywords, item.code, item.support_item_number].filter(Boolean).join(' ') };
  }
  const value = String(item.id ?? item._id ?? '');
  const code = item.ndis_support_item_number || item.ndis_code || item.support_item_number || item.item_number || item.code || '';
  const rate = item.rate_per_unit ?? item.rate ?? item.hourly_rate;
  const rateText = showRate && rate !== undefined && rate !== null && rate !== '' ? ` ($${Number(rate).toFixed(2)}${rateSuffix})` : '';
  return {
    value,
    label: `${item.name || item.label || 'Line item'}${code ? ` · ${code}` : ''}${rateText}`,
    keywords: [item.code, item.ndis_code, item.item_number, item.support_item_number, item.ndis_support_item_number, item.support_item_name, item.category, item.registration_group]
      .filter(Boolean).join(' '),
    disabled: item.disabled,
  };
}

export default function LineItemSelect({
  items = [], options, value, onChange, showRate = true, rateSuffix = '',
  placeholder = 'Select a line item', emptyText = 'No line items match that name or code', ...rest
}) {
  const opts = useMemo(
    () => (options || items).map(i => lineItemOption(i, { showRate, rateSuffix })).filter(Boolean),
    [items, options, showRate, rateSuffix],
  );
  return (
    <SearchSelect options={opts} value={value} onChange={onChange} placeholder={placeholder}
      searchPlaceholder="Search by name or code…" emptyText={emptyText} {...rest} />
  );
}
